import { useEffect, useState } from "react";
import { Modal, Text, Flex, Paper, Button, Divider, Tooltip } from "@mantine/core";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { errorNotification } from "@/shared/utils/errorNotification";

interface PaymentMethodModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (method: "credits") => void;
  loading?: boolean;
  creditsRequired?: number;
  showNote?: boolean;
  /** Wording for the insufficient-balance toast (modal is also used for verification). */
  insufficientCreditsTone?: "collaboration" | "generic";
}

function PaymentMethodModal({
  opened,
  onClose,
  onConfirm,
  loading = false,
  creditsRequired = 14,
  showNote = true,
  insufficientCreditsTone = "collaboration",
}: PaymentMethodModalProps) {
  const [availableCredits, setAvailableCredits] = useState(0);

  useEffect(() => {
    if (!opened) return;

    let cancelled = false;

    const fetchWalletBalance = async () => {
      try {
        const walletData = await trpc.wallet.getBalance.query();
        if (cancelled) return;

        const available = walletData.available || 0;
        setAvailableCredits(available);

        if (available < creditsRequired) {
          const need = creditsRequired - available;
          errorNotification({
            title: "Insufficient credits",
            message:
              insufficientCreditsTone === "collaboration"
                ? `Your credit is less than required for this collaboration. You have ${available} credits but need ${creditsRequired} (${need} more). Please add credits to continue.`
                : `Your credit is less than required to complete this payment. You have ${available} credits but need ${creditsRequired} (${need} more). Please add credits to continue.`,
          });
        }
      } catch (error) {
        if (cancelled) return;
        handleTrpcError(error);
        setAvailableCredits(0);
      }
    };

    fetchWalletBalance();
    return () => {
      cancelled = true;
    };
  }, [opened, creditsRequired, insufficientCreditsTone]);

  const remainingBalance = availableCredits - creditsRequired;
  const hasEnoughCredits = availableCredits >= creditsRequired;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} fz="lg">
          Confirm Credit Usage
        </Text>
      }
      centered
      radius="md"
      size="md"
    >
      <Text c="dimmed" fz="sm" mb="lg">
        You are about to use {creditsRequired} credits to activate this collaboration.
      </Text>

      <Paper
        radius="md"
        style={{ border: "1px solid var(--mantine-color-default-border)" }}
      >
        <Flex justify="space-between" align="center" px="md" py="sm">
          <Text fz="sm">Available Credits:</Text>
          <Text fz="sm">{availableCredits}</Text>
        </Flex>
        <Divider />
        <Flex justify="space-between" align="center" px="md" py="sm">
          <Text fz="sm">Credits Required:</Text>
          <Text fz="sm">{creditsRequired}</Text>
        </Flex>
        <Divider />
        <Flex justify="space-between" align="center" px="md" py="sm">
          <Text fz="sm" fw={700}>
            Remaining Balance:
          </Text>
          <Text fz="sm" fw={700} c={remainingBalance >= 0 ? "green" : "red"}>
            {remainingBalance}
          </Text>
        </Flex>
      </Paper>

      {showNote && (
        <Text fz="xs" c="dimmed" mt="md">
          <Text span fw={700} fz="xs" c="dimmed">
            Note:
          </Text>{" "}
          If the collaboration is unsuccessful, your credits will be automatically refunded to your
          wallet for future use.
        </Text>
      )}

      <Tooltip label="Insufficient Credits" disabled={hasEnoughCredits} withArrow>
        <Button
          fullWidth
          mt="xl"
          onClick={() => onConfirm("credits")}
          disabled={!hasEnoughCredits}
          loading={loading}
          data-disabled={!hasEnoughCredits || undefined}
        >
          Confirm & Proceed
        </Button>
      </Tooltip>
    </Modal>
  );
}

export default PaymentMethodModal;
